import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react-native';
import { useEffect } from 'react';

import {
  ProjectListNodeUpdatedDocument,
  ProjectListNodeUpdatedSubscription,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { emitProjectUpdated } from '@utils/projectUpdatedEvent';

import type { ObservableSubscription } from '@apollo/client';

/**
 * TA-11139 (web): the app-wide `projectUpdated` subscription that feeds
 * projectUpdatedEvent, so web My Tasks (Tasks.web) refreshes on a task change
 * the way native does. Native gets the same events from
 * useProjectMemberAddedNotification, which is mounted only in the native
 * AppStack. Web mounts this instead, so the web member-added toast flow is
 * left alone.
 *
 * Uses the light ProjectListNode selection: My Tasks only needs the project
 * id. Same error handling as the native observer: an `error` callback (a
 * missing one makes zen-observable throw, TA-10117), then a resubscribe with
 * backoff of 2 s, 4 s, 8 s… capped at 60 s.
 */
export const useProjectUpdatedBroadcast = () => {
  const client = useApolloClient();
  const { me } = useMe();
  const meId = me?.id;

  useEffect(() => {
    if (!meId) return undefined;

    let disposed = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let subscription: ObservableSubscription | undefined;

    const subscribe = () => {
      subscription = client
        .subscribe<ProjectListNodeUpdatedSubscription>({
          query: ProjectListNodeUpdatedDocument,
        })
        .subscribe({
          next({ data }) {
            if (disposed) return;
            attempt = 0;
            const projectId = data?.projectUpdated?.id;
            if (projectId) emitProjectUpdated(projectId);
          },
          error(err) {
            Sentry.captureMessage(
              `projectUpdated broadcast subscription error: ${
                err instanceof Error ? err.message : String(err)
              }`,
              'warning'
            );
            if (disposed) return;
            const delay = Math.min(2000 * 2 ** attempt, 60000);
            attempt += 1;
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(() => {
              retryTimer = undefined;
              if (!disposed) subscribe();
            }, delay);
          },
        });
    };

    subscribe();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      subscription?.unsubscribe();
    };
  }, [client, meId]);
};
